import React from 'react'
import PropTypes from 'prop-types'
import { Modal, Table, Button, Form, Input, Select, Upload, Tag, Popconfirm, Tooltip, Space, message } from 'antd'
import { UploadOutlined, DeleteOutlined, EyeOutlined, EditOutlined, ArrowLeftOutlined, DownloadOutlined, StopOutlined, CheckCircleOutlined, FormOutlined, OrderedListOutlined } from '@ant-design/icons'
import PdfPlaceholderEditor from '../../../../Common/PdfPlaceholderEditor'
import DocumentOrderEditor from './DocumentOrderEditor'
import {
  getRamsDocumentPdfInfo,
  getRamsDocumentPdfContent,
  saveRamsPlaceholderConfig,
  previewRamsPlaceholders,
  updateRamsDocument,
} from '../../../../HttpRequests/ramsControlCentre'

var Option = Select.Option
var TextArea = Input.TextArea

var DOCUMENT_TYPES = [
  { value: 'header', label: 'Header' },
  { value: 'footer', label: 'Footer' },
  { value: 'service', label: 'Service' },
  { value: 'access', label: 'Equipment' },
  { value: 'paint', label: 'Paint' },
  { value: 'ppe', label: 'PPE' },
  { value: 'task', label: 'Task' },
  { value: 'general', label: 'General' },
]

export default class _DocumentsModal_ extends React.PureComponent {

  static propTypes = {
    visible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    documents: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    uploading: PropTypes.bool,
    onUpload: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onToggleActive: PropTypes.func.isRequired,
    onRefresh: PropTypes.func.isRequired,
    serviceTypes: PropTypes.array,
    userSettings: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  }

  scrollRef = React.createRef()

  scrollToTop = () => {
    if (this.scrollRef.current && this.scrollRef.current.parentElement) {
      this.scrollRef.current.parentElement.scrollTop = 0
    }
  }

  state = {
    showUploadForm: false,
    title: '',
    description: '',
    documentType: 'general',
    typeCode: [],
    fileList: [],
    // Order editor state
    orderEditorVisible: false,
    // Placeholder editor state
    editorVisible: false,
    editorDocumentId: null,
    editorDocumentTitle: '',
    editorLoading: false,
    editorSaving: false,
    editorPdfUrl: null,
    editorPages: [],
    editorPlaceholderConfig: null,
    // Edit/re-upload state
    editingDocumentId: null,
    editDocumentType: 'general',
    editTypeCode: [],
    editChangeNotes: '',
    editFileList: [],
    editSubmitting: false,
  }

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.setState({
        orderEditorVisible: false,
        editorVisible: false,
        editorDocumentId: null,
        editorDocumentTitle: '',
        editorPdfUrl: null,
        editorPdfData: null,
        editorPages: [],
        editorPlaceholderConfig: null,
      })
    }
  }

  handleUpload = () => {
    var { title, documentType, typeCode, fileList } = this.state

    if (!title || fileList.length === 0) {
      message.warning('Please enter a title and select a file')
      return
    }

    var formData = new FormData()
    formData.append('title', title)
    formData.append('document_type', documentType)
    formData.append('file', fileList[0])
    if (['service', 'access', 'paint', 'ppe', 'task'].indexOf(documentType) !== -1 && typeCode && typeCode.length > 0) {
      formData.append('type_code', JSON.stringify(typeCode))
    }

    var self = this
    this.props.onUpload(formData)
      .then(function() {
        message.success('Document uploaded successfully')
        self.setState({
          showUploadForm: false,
          title: '',
          description: '',
          documentType: 'general',
          typeCode: [],
          fileList: [],
        })
        self.props.onRefresh()
      })
      .catch(function() {
        message.error('Failed to upload document')
      })
  }

  handleDelete = (id) => {
    var self = this
    this.props.onDelete(id)
      .then(function() {
        message.success('Document deleted')
      })
      .catch(function() {
        message.error('Failed to delete document')
      })
  }

  handleToggleActive = (id) => {
    this.props.onToggleActive(id)
  }

  handleEdit = (record) => {
    this.setState({
      editingDocumentId: record.id,
      editDocumentType: record.document_type || 'general',
      editTypeCode: Array.isArray(record.type_code) ? record.type_code : (record.type_code ? [record.type_code] : []),
      editChangeNotes: '',
      editFileList: [],
      editSubmitting: false,
    }, this.scrollToTop)
  }

  handleCancelEdit = () => {
    this.setState({
      editingDocumentId: null,
      editDocumentType: 'general',
      editTypeCode: [],
      editChangeNotes: '',
      editFileList: [],
      editSubmitting: false,
    })
  }

  handleEditSubmit = () => {
    var self = this
    var { editingDocumentId, editDocumentType, editTypeCode, editChangeNotes, editFileList } = this.state

    if (!editChangeNotes) {
      message.warning('Please enter change notes')
      return
    }

    var formData = new FormData()
    formData.append('change_notes', editChangeNotes)
    formData.append('document_type', editDocumentType)
    if (['service', 'access', 'paint', 'ppe', 'task'].indexOf(editDocumentType) !== -1 && editTypeCode && editTypeCode.length > 0) {
      formData.append('type_code', JSON.stringify(editTypeCode))
    }
    if (editFileList.length > 0) {
      formData.append('file', editFileList[0])
    }

    self.setState({ editSubmitting: true })

    updateRamsDocument(editingDocumentId, formData)
      .then(function() {
        message.success('Document updated — new version created')
        self.setState({
          editingDocumentId: null,
          editDocumentType: 'general',
          editTypeCode: [],
          editChangeNotes: '',
          editFileList: [],
          editSubmitting: false,
        })
        self.props.onRefresh()
      })
      .catch(function() {
        message.error('Failed to update document')
        self.setState({ editSubmitting: false })
      })
  }

  handleOpenEditor = (record) => {
    var self = this
    this.setState({
      editorVisible: true,
      editorDocumentId: record.id,
      editorDocumentTitle: record.title,
      editorLoading: true,
      editorPdfUrl: null,
      editorPdfData: null,
      editorPages: [],
      editorPlaceholderConfig: null,
    })

    // Fetch PDF info and content in parallel (content proxied through backend to avoid CORS)
    Promise.all([
      getRamsDocumentPdfInfo(record.id),
      getRamsDocumentPdfContent(record.id),
    ]).then(function(results) {
      var infoResponse = results[0]
      var contentResponse = results[1]
      self.setState({
        editorLoading: false,
        editorPages: (infoResponse.data && infoResponse.data.pages) || [],
        editorPlaceholderConfig: (infoResponse.data && infoResponse.data.placeholder_config) || { placeholders: [] },
        editorPdfData: contentResponse.data || null,
      })
    }).catch(function(err) {
      self.setState({ editorLoading: false })
      message.error('Failed to load PDF information')
    })
  }

  handleCloseEditor = () => {
    this.setState({
      editorVisible: false,
      editorDocumentId: null,
      editorDocumentTitle: '',
      editorPdfUrl: null,
      editorPages: [],
      editorPlaceholderConfig: null,
    })
    if (this.props.onRefresh) {
      this.props.onRefresh()
    }
  }

  handleSavePlaceholderConfig = (config) => {
    var self = this
    var documentId = this.state.editorDocumentId
    this.setState({ editorSaving: true })

    saveRamsPlaceholderConfig(documentId, config)
      .then(function() {
        self.setState({
          editorSaving: false,
          editorPlaceholderConfig: config,
        })
        message.success('Field configuration saved')
      })
      .catch(function() {
        self.setState({ editorSaving: false })
        message.error('Failed to save field configuration')
      })
  }

  handlePreviewPlaceholders = () => {
    var documentId = this.state.editorDocumentId
    previewRamsPlaceholders(documentId)
      .then(function(response) {
        var blob = new Blob([response.data], { type: 'application/pdf' })
        var url = window.URL.createObjectURL(blob)
        window.open(url, '_blank')
      })
      .catch(function() {
        message.error('Failed to generate preview')
      })
  }

  getColumns = () => {
    var self = this
    return [
      {
        title: 'Title',
        dataIndex: 'title',
        key: 'title',
      },
      {
        title: 'Type',
        dataIndex: 'document_type',
        key: 'document_type',
        width: 120,
        render: function(text) {
          var found = DOCUMENT_TYPES.find(function(t) { return t.value === text })
          return found ? found.label : text
        },
      },
      {
        title: 'Sub types',
        key: 'associated_items',
        width: 200,
        render: function(text, record) {
          var docType = record.document_type
          var userSettings = self.props.userSettings || {}
          var typeCodes = Array.isArray(record.type_code) ? record.type_code : (record.type_code ? [record.type_code] : [])

          if (typeCodes.length === 0) {
            return '-'
          }

          var labels = []
          if (docType === 'service') {
            var serviceTypes = self.props.serviceTypes || []
            typeCodes.forEach(function(code) {
              var found = serviceTypes.find(function(st) { return st.key === code })
              labels.push(found ? (code + ' - ' + found.title) : code)
            })
          } else if (docType === 'access') {
            var accessItems = (userSettings && userSettings.access) || []
            typeCodes.forEach(function(code) {
              var foundAccess = accessItems.find(function(item) { return item.key === code })
              labels.push(foundAccess ? foundAccess.value : code)
            })
          } else if (docType === 'paint') {
            var paintItems = (userSettings && userSettings.paints) || []
            typeCodes.forEach(function(code) {
              var foundPaint = paintItems.find(function(item) { return item.name === code })
              labels.push(foundPaint ? foundPaint.name : code)
            })
          } else if (docType === 'ppe') {
            var ppeItems = (userSettings && userSettings.ppe) || []
            typeCodes.forEach(function(code) {
              var foundPpe = ppeItems.find(function(item) { return item.key === code })
              labels.push(foundPpe ? foundPpe.value : code)
            })
          } else if (docType === 'task') {
            var taskItems = (userSettings && userSettings.tasks) || []
            typeCodes.forEach(function(code) {
              var foundTask = taskItems.find(function(item) { return item.key === code })
              labels.push(foundTask ? foundTask.value : code)
            })
          }

          return labels.length > 0 ? labels.join(', ') : '-'
        },
      },
      {
        title: 'Version',
        dataIndex: 'version',
        key: 'version',
        width: 100,
        render: function(text, record) {
          var versions = record.recent_versions || []
          if (versions.length === 0) {
            return 'v' + (text || 1)
          }
          var tooltipContent = React.createElement('div', { style: { fontSize: 12 } },
            React.createElement('div', { style: { fontWeight: 'bold', marginBottom: 6, borderBottom: '1px solid rgba(255,255,255,0.3)', paddingBottom: 4 } }, 'Recent Revisions'),
            versions.map(function(v, idx) {
              return React.createElement('div', { key: idx, style: { marginBottom: idx < versions.length - 1 ? 6 : 0 } },
                React.createElement('div', { style: { fontWeight: 'bold' } }, 'v' + v.version + ' — ' + (v.created_at || '')),
                React.createElement('div', { style: { color: 'rgba(255,255,255,0.8)' } }, v.created_by || ''),
                React.createElement('div', { style: { color: 'rgba(255,255,255,0.65)', fontStyle: 'italic' } }, v.change_notes || '')
              )
            })
          )
          return React.createElement(Tooltip, { title: tooltipContent, placement: 'right' },
            React.createElement('span', { style: { cursor: 'pointer', borderBottom: '1px dashed #1890ff', color: '#1890ff' } }, 'v' + (text || 1))
          )
        },
      },
      {
        title: 'Size',
        dataIndex: 'formatted_file_size',
        key: 'size',
        width: 100,
      },
      {
        title: 'Status',
        dataIndex: 'active',
        key: 'active',
        width: 100,
        render: function(active) {
          return active
            ? <Tag color="green">Active</Tag>
            : <Tag color="red">Inactive</Tag>
        },
      },
      {
        title: 'Fields',
        key: 'placeholders',
        width: 90,
        render: function(text, record) {
          var config = record.placeholder_config
          var count = (config && config.placeholders) ? config.placeholders.length : 0
          return count > 0
            ? <Tag color="blue">{count}</Tag>
            : <Tag>None</Tag>
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 200,
        render: function(text, record) {
          return (
            <Space>
              <Tooltip title="Edit / Re-upload">
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={function() { self.handleEdit(record) }}
                />
              </Tooltip>
              <Tooltip title="Edit Fields">
                <Button
                  type="link"
                  size="small"
                  icon={<FormOutlined />}
                  onClick={function() { self.handleOpenEditor(record) }}
                />
              </Tooltip>
              {record.file_url && (
                <Tooltip title="View Document">
                  <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={function() { window.open(record.file_url, '_blank') }}
                  />
                </Tooltip>
              )}
              <Tooltip title="Download Document">
                <Button
                  type="link"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={function() {
                    if (record.file_url) {
                      var link = document.createElement('a')
                      link.href = record.file_url
                      link.download = record.title + '.pdf'
                      link.click()
                    }
                  }}
                />
              </Tooltip>
              <Tooltip title={record.active ? 'Deactivate Document' : 'Activate Document'}>
                <Button
                  type="link"
                  size="small"
                  icon={record.active ? <StopOutlined /> : <CheckCircleOutlined />}
                  onClick={function() { self.handleToggleActive(record.id) }}
                />
              </Tooltip>
              <Popconfirm
                title="Delete this document?"
                onConfirm={function() { self.handleDelete(record.id) }}
              >
                <Tooltip title="Delete Document">
                  <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            </Space>
          )
        },
      },
    ]
  }

  renderDocumentList = () => {
    var { documents, loading, uploading, serviceTypes, userSettings } = this.props
    var { showUploadForm, title, description, documentType, typeCode, fileList } = this.state
    var equipmentItems = (userSettings && userSettings.access) || []
    var paintItems = (userSettings && userSettings.paints) || []
    var ppeItems = (userSettings && userSettings.ppe) || []
    var taskItems = (userSettings && userSettings.tasks) || []
    var self = this

    return (
      <div ref={this.scrollRef}>
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            onClick={function() { self.setState({ showUploadForm: !showUploadForm }, self.scrollToTop) }}
            style={{ marginRight: 8 }}
          >
            <UploadOutlined />
            {showUploadForm ? 'Cancel Upload' : 'Upload Document'}
          </Button>
          <Button
            onClick={function() { self.setState({ orderEditorVisible: true }, self.scrollToTop) }}
            icon={<OrderedListOutlined />}
          >
            Document Order
          </Button>
        </div>

        {showUploadForm && (
          <div style={{ marginBottom: 24, padding: 16, background: '#fafafa', borderRadius: 4 }}>
            <Form layout="vertical">
              <Form.Item label="Title" required>
                <Input
                  value={title}
                  onChange={function(e) { self.setState({ title: e.target.value }) }}
                  placeholder="Document title"
                />
              </Form.Item>
              <Form.Item label="Description">
                <TextArea
                  value={description}
                  onChange={function(e) { self.setState({ description: e.target.value }) }}
                  placeholder="Optional description"
                  rows={2}
                />
              </Form.Item>
              <Form.Item label="Document Type" required>
                <Select
                  value={documentType}
                  onChange={function(val) { self.setState({ documentType: val, typeCode: [] }) }}
                >
                  {DOCUMENT_TYPES.map(function(type) {
                    return <Option key={type.value} value={type.value}>{type.label}</Option>
                  })}
                </Select>
              </Form.Item>
              {documentType === 'service' && (
                <Form.Item label="Services" required>
                  <Select
                    mode="multiple"
                    value={typeCode}
                    onChange={function(val) { self.setState({ typeCode: val }) }}
                    placeholder="Select services"
                    showSearch
                    optionFilterProp="children"
                  >
                    {(serviceTypes || []).map(function(st) {
                      return <Option key={st.key} value={st.key}>{st.key} - {st.title}</Option>
                    })}
                  </Select>
                </Form.Item>
              )}
              {documentType === 'access' && (
                <Form.Item label="Equipment" required>
                  <Select
                    mode="multiple"
                    value={typeCode}
                    onChange={function(val) { self.setState({ typeCode: val }) }}
                    placeholder="Select equipment"
                    showSearch
                    optionFilterProp="children"
                  >
                    {equipmentItems.map(function(item) {
                      return <Option key={item.key} value={item.key}>{item.value}</Option>
                    })}
                  </Select>
                </Form.Item>
              )}
              {documentType === 'paint' && (
                <Form.Item label="Paints" required>
                  <Select
                    mode="multiple"
                    value={typeCode}
                    onChange={function(val) { self.setState({ typeCode: val }) }}
                    placeholder="Select paints"
                    showSearch
                    optionFilterProp="children"
                  >
                    {paintItems.map(function(item) {
                      return <Option key={item.name} value={item.name}>{item.name}</Option>
                    })}
                  </Select>
                </Form.Item>
              )}
              {documentType === 'ppe' && (
                <Form.Item label="PPE Items" required>
                  <Select
                    mode="multiple"
                    value={typeCode}
                    onChange={function(val) { self.setState({ typeCode: val }) }}
                    placeholder="Select PPE items"
                    showSearch
                    optionFilterProp="children"
                  >
                    {ppeItems.map(function(item) {
                      return <Option key={item.key} value={item.key}>{item.value}</Option>
                    })}
                  </Select>
                </Form.Item>
              )}
              {documentType === 'task' && (
                <Form.Item label="Task" required>
                  <Select
                    mode="multiple"
                    value={typeCode}
                    onChange={function(val) { self.setState({ typeCode: val }) }}
                    placeholder="Select task"
                    showSearch
                    optionFilterProp="children"
                  >
                    {taskItems.map(function(item) {
                      return <Option key={item.key} value={item.key}>{item.value}</Option>
                    })}
                  </Select>
                </Form.Item>
              )}
              <Form.Item label="File" required>
                <Upload
                  beforeUpload={function(file) {
                    self.setState({ fileList: [file] })
                    return false
                  }}
                  fileList={fileList}
                  onRemove={function() { self.setState({ fileList: [] }) }}
                  accept=".pdf,.doc,.docx"
                >
                  <Button icon={<UploadOutlined />}>Select File</Button>
                </Upload>
              </Form.Item>
              <Button type="primary" onClick={this.handleUpload} loading={uploading}>
                Upload
              </Button>
            </Form>
          </div>
        )}

        {this.state.editingDocumentId && (
          <div style={{ marginBottom: 24, padding: 16, background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4 }}>
            <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
              Edit Document (ID: {this.state.editingDocumentId})
            </div>
            <Form layout="vertical">
              <Form.Item label="Document Type">
                <Select
                  value={this.state.editDocumentType}
                  onChange={function(val) { self.setState({ editDocumentType: val, editTypeCode: [] }) }}
                >
                  {DOCUMENT_TYPES.map(function(type) {
                    return <Option key={type.value} value={type.value}>{type.label}</Option>
                  })}
                </Select>
              </Form.Item>
              {this.state.editDocumentType === 'service' && (
                <Form.Item label="Services" required>
                  <Select
                    mode="multiple"
                    value={this.state.editTypeCode}
                    onChange={function(val) { self.setState({ editTypeCode: val }) }}
                    placeholder="Select services"
                    showSearch
                    optionFilterProp="children"
                  >
                    {(serviceTypes || []).map(function(st) {
                      return <Option key={st.key} value={st.key}>{st.key} - {st.title}</Option>
                    })}
                  </Select>
                </Form.Item>
              )}
              {this.state.editDocumentType === 'access' && (
                <Form.Item label="Equipment" required>
                  <Select
                    mode="multiple"
                    value={this.state.editTypeCode}
                    onChange={function(val) { self.setState({ editTypeCode: val }) }}
                    placeholder="Select equipment"
                    showSearch
                    optionFilterProp="children"
                  >
                    {equipmentItems.map(function(item) {
                      return <Option key={item.key} value={item.key}>{item.value}</Option>
                    })}
                  </Select>
                </Form.Item>
              )}
              {this.state.editDocumentType === 'paint' && (
                <Form.Item label="Paints" required>
                  <Select
                    mode="multiple"
                    value={this.state.editTypeCode}
                    onChange={function(val) { self.setState({ editTypeCode: val }) }}
                    placeholder="Select paints"
                    showSearch
                    optionFilterProp="children"
                  >
                    {paintItems.map(function(item) {
                      return <Option key={item.name} value={item.name}>{item.name}</Option>
                    })}
                  </Select>
                </Form.Item>
              )}
              {this.state.editDocumentType === 'ppe' && (
                <Form.Item label="PPE Items" required>
                  <Select
                    mode="multiple"
                    value={this.state.editTypeCode}
                    onChange={function(val) { self.setState({ editTypeCode: val }) }}
                    placeholder="Select PPE items"
                    showSearch
                    optionFilterProp="children"
                  >
                    {ppeItems.map(function(item) {
                      return <Option key={item.key} value={item.key}>{item.value}</Option>
                    })}
                  </Select>
                </Form.Item>
              )}
              {this.state.editDocumentType === 'task' && (
                <Form.Item label="Task" required>
                  <Select
                    mode="multiple"
                    value={this.state.editTypeCode}
                    onChange={function(val) { self.setState({ editTypeCode: val }) }}
                    placeholder="Select task"
                    showSearch
                    optionFilterProp="children"
                  >
                    {taskItems.map(function(item) {
                      return <Option key={item.key} value={item.key}>{item.value}</Option>
                    })}
                  </Select>
                </Form.Item>
              )}
              <Form.Item label="Change Notes" required>
                <TextArea
                  value={this.state.editChangeNotes}
                  onChange={function(e) { self.setState({ editChangeNotes: e.target.value }) }}
                  placeholder="Describe what changed in this version"
                  rows={2}
                />
              </Form.Item>
              <Form.Item label="New File (PDF or Word)">
                <Upload
                  beforeUpload={function(file) {
                    self.setState({ editFileList: [file] })
                    return false
                  }}
                  fileList={this.state.editFileList}
                  onRemove={function() { self.setState({ editFileList: [] }) }}
                  accept=".pdf,.doc,.docx"
                >
                  <Button icon={<UploadOutlined />}>Select File</Button>
                </Upload>
              </Form.Item>
              <Button type="primary" onClick={this.handleEditSubmit} loading={this.state.editSubmitting} style={{ marginRight: 8 }}>
                Save Changes
              </Button>
              <Button onClick={this.handleCancelEdit}>
                Cancel
              </Button>
            </Form>
          </div>
        )}

        <Table
          rowKey="id"
          columns={this.getColumns()}
          dataSource={documents}
          loading={loading}
          size="small"
          pagination={false}
        />
      </div>
    )
  }

  renderPlaceholderEditor = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #e8e8e8', display: 'flex', alignItems: 'center' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={this.handleCloseEditor}
            size="small"
          >
            Back to Documents
          </Button>
          <span style={{ marginLeft: 12, fontWeight: 'bold' }}>
            Editing Fields: {this.state.editorDocumentTitle}
          </span>
        </div>

        <div style={{ flex: 1, overflow: 'hidden' }}>
          <PdfPlaceholderEditor
            pdfData={this.state.editorPdfData}
            pages={this.state.editorPages}
            placeholderConfig={this.state.editorPlaceholderConfig}
            onSave={this.handleSavePlaceholderConfig}
            onPreview={this.handlePreviewPlaceholders}
            loading={this.state.editorLoading}
            saving={this.state.editorSaving}
          />
        </div>
      </div>
    )
  }

  render () {
    var { visible, onClose } = this.props
    var self = this
    var editorVisible = this.state.editorVisible
    var orderEditorVisible = this.state.orderEditorVisible
    var modalTitle = editorVisible ? 'Edit Fields' : orderEditorVisible ? 'Document Order' : 'RAMS Document Library'

    var modalWidth = editorVisible ? '100%' : 1200
    var bodyStyle = editorVisible
      ? { padding: 0, height: 'calc(100vh - 160px)', overflow: 'hidden' }
      : { maxHeight: '80vh', overflowY: 'auto' }

    return (
      <Modal
        title={modalTitle}
        visible={visible}
        onCancel={onClose}
        footer={null}
        width={modalWidth}
        bodyStyle={bodyStyle}
        style={editorVisible ? { top: 20 } : undefined}
      >
        {editorVisible
          ? this.renderPlaceholderEditor()
          : orderEditorVisible
            ? <DocumentOrderEditor
                documents={this.props.documents || []}
                serviceTypes={this.props.serviceTypes || []}
                userSettings={this.props.userSettings || {}}
                onBack={function() { self.setState({ orderEditorVisible: false }) }}
              />
            : this.renderDocumentList()}
      </Modal>
    )
  }
}
